'use client';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { cn } from 'lib/cn';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useMediaQuery from '~/hooks/useMediaQuery';

export const TracingBeam = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start']
    });

    const contentRef = useRef<HTMLDivElement>(null);
    const [svgHeight, setSvgHeight] = useState(0);

    useEffect(() => {
        if (contentRef.current) {
            setSvgHeight(contentRef.current.offsetHeight);
        }
    }, []);

    const y1 = useSpring(useTransform(scrollYProgress, [0, 0.7], [0, svgHeight]), {
        stiffness: 500,
        damping: 90
    });
    const y2 = useSpring(useTransform(scrollYProgress, [0, 1.2], [0, svgHeight - 200]), {
        stiffness: 500,
        damping: 90
    });

    const pathD = useMemo(() => {
        const height = Math.max(svgHeight, 1);
        const startY = -36;
        const centerX = 10;
        const leftX = 3;
        const rightX = 17;
        const segment = 800; // Distance between curves
        const curves = Math.max(1, Math.floor((height - startY) / segment));

        let path = `M ${centerX} 0 V ${startY}`;
        let currentY = startY;

        for (let i = 0; i < curves; i++) {
            const goRight = i % 2 === 0;
            const endX = goRight ? rightX : leftX;
            const nextY = Math.min(height, startY + segment * (i + 1));
            const midY = currentY + (nextY - currentY) * 0.5;

            // The "Curve Zone" height
            const curveDepth = 24;

            // 1. Go straight down the center
            path += ` L ${centerX} ${midY - curveDepth}`;

            // 2. Curve OUT (Cubic Bezier for smooth vertical start/end)
            // C cp1x cp1y cp2x cp2y x y
            path += ` C ${centerX} ${midY - curveDepth / 2} ${endX} ${midY - curveDepth / 2} ${endX} ${midY}`;

            // 3. Go straight down the side
            path += ` L ${endX} ${nextY - curveDepth}`;

            // 4. Curve BACK IN to center
            path += ` C ${endX} ${nextY - curveDepth / 2} ${centerX} ${nextY - curveDepth / 2} ${centerX} ${nextY}`;

            currentY = nextY;
        }

        // Finish straight line to the very bottom
        if (currentY < height) {
            path += ` L ${centerX} ${height}`;
        }

        return path;
    }, [svgHeight]);

    if (isMobile) return children;

    return (
        <motion.div ref={ref} className={cn('tw-relative tw-mx-auto tw-h-full tw-w-full', className)}>
            <div className="-tw-left-15 tw-absolute tw-hidden md:-tw-left-[7%] md:tw-block">
                <motion.div
                    transition={{
                        duration: 0.2,
                        delay: 0.2
                    }}
                    animate={{
                        boxShadow: scrollYProgress.get() > 0 ? 'none' : 'rgba(0, 0, 0, 0.24) 0px 3px 8px'
                    }}
                >
                    <motion.div
                        transition={{
                            duration: 0.2,
                            delay: 0.5
                        }}
                        animate={{}}
                    />
                </motion.div>

                <svg
                    viewBox={`0 0 20 ${svgHeight}`}
                    width="20"
                    height={svgHeight}
                    className="tw-ml-4 tw-block"
                    aria-hidden="true"
                >
                    <motion.path
                        d={pathD}
                        fill="none"
                        stroke="#9091A0"
                        strokeOpacity="0.16"
                        transition={{ duration: 10 }}
                    ></motion.path>

                    <motion.path
                        d={pathD}
                        fill="none"
                        stroke="url(#gradient)"
                        strokeWidth="1.25"
                        className="motion-reduce:tw-hidden"
                        transition={{ duration: 10 }}
                    ></motion.path>

                    <defs>
                        <motion.linearGradient
                            id="gradient"
                            gradientUnits="userSpaceOnUse"
                            x1="0"
                            x2="0"
                            y1={y1}
                            y2={y2}
                        >
                            <stop stopColor="#38c172" stopOpacity="0" />
                            <stop stopColor="#38c172" />
                            <stop offset="0.325" stopColor="#38c172" />
                            <stop offset="1" stopColor="#DAFBE8" stopOpacity="0" />
                        </motion.linearGradient>
                    </defs>
                </svg>
            </div>

            <div ref={contentRef}>{children}</div>
        </motion.div>
    );
};
